import { ListingTags } from "./ListingTags";
import { ListingMatchField } from "./ListingMatchField";
import { ListingSearchTargetField } from "./ListingSearchTargetField";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { PersonLink } from "@/components/common/PersonLink";
import { buttonClasses } from "@/components/design/button-classes";
import { ShareUrlButton } from "@/components/share/ShareUrlButton";
import { ListingState } from "@/graphql/generated/operations";
import { formatDateTime } from "@/lib/format-date";
import type { DashboardContext } from "@/pages/project/dashboard-kinds";
import { ListingConfigurationField } from "./ListingConfigurationField";
import { ListingField } from "./ListingField";
import { ListingReference } from "./ListingReference";
import { LISTING_STATE_LABELS } from "./listing-state";
import { ListingStateActions } from "./ListingStateActions";
import { useListing } from "./use-listing";

// Die Übersicht einer Ausschreibung im Projekt-Dashboard: Kennziffer zum Kopieren, Zustand mit seinen
// Wechseln, Verantwortlich, Frist, Veröffentlichung, Kategorien, Leserschaft und der Weg zum "Post", wie Bewerbende ihn lesen. Was bedienbar ist, steht in `Listing.actions`;
// die Felder prüfen keine Rolle und keinen Zustand selbst
// (@external-adr GENESIS-0070-a-listing-is-a-kind-of-project-not-a-shared-item §6).
//
// Aufbewahrung, Leserschaft und Ersteller liest genesis nur Eingetragenen vor. Für einen
// Gruppen-Empfänger sind sie null, und die Felder fehlen dann ganz, statt "niemand" zu behaupten.
export function ListingOverviewFields({ ctx }: { ctx: DashboardContext; onOpenSettings?: () => void }) {
  const intl = useIntl();
  // Dieselbe Abfrage wie die Quelle des Dashboards, also aus dem Cache.
  const { listing, transition } = useListing(ctx.entity.id);
  if (!listing) return null;

  return (
    <div className="max-w-5xl">
    <div className="grid grid-cols-1 items-start gap-8 @sm:grid-cols-2 @lg:grid-cols-3">
      <ListingSearchTargetField ctx={ctx} />
      <ListingReference reference={listing.reference} variant="field" />
      <ListingField label={<FormattedMessage id="share.listings.status" defaultMessage="Zustand" />}>
        {listing.state ? (
          <span id="listing-state" className="font-medium">
            {intl.formatMessage(LISTING_STATE_LABELS[listing.state])}
          </span>
        ) : (
          "–"
        )}
        <div className="mt-2 flex flex-wrap items-start gap-2">
          <ListingStateActions actions={listing.actions} deadline={listing.deadline} onTransition={transition} />
          {listing.state !== ListingState.Open ? (
            <Link
              id="listing-preview-link"
              to={`/share/listings/${listing.id}/post`}
              className={buttonClasses({ variant: "secondary", size: "compact" })}
            >
              <FormattedMessage id="share.listings.post.previewLabel" defaultMessage="Vorschau" />
            </Link>
          ) : null}
        </div>
      </ListingField>
      {ctx.entity.createdBy ? (
        <ListingField label={<FormattedMessage id="project.meta.owner" defaultMessage="Verantwortlich" />}>
          <PersonLink person={ctx.entity.createdBy} size="sm" />
        </ListingField>
      ) : null}
      <ListingConfigurationField key={listing.deadline} listing={listing} section="deadline" label={intl.formatMessage({ id: "share.listings.field.deadline" })} />
      <ListingField label={<FormattedMessage id="share.listings.field.publishedOn" defaultMessage="Veröffentlicht am" />}>
        {formatDateTime(listing.publishedOn, intl.locale) || "–"}
      </ListingField>
      {listing.concludedOn ? (
        <ListingField label={<FormattedMessage id="share.listings.field.concludedOn" defaultMessage="Abgeschlossen am" />}>
          {formatDateTime(listing.concludedOn, intl.locale)}
        </ListingField>
      ) : null}
      {listing.retentionDays != null ? <ListingConfigurationField key={listing.retentionDays} listing={listing} section="retention"
        label={intl.formatMessage({ id: "share.listings.field.retention" })} /> : null}
      <ListingTags data={ctx.data} onChange={ctx.editData} readOnly={ctx.readOnly} />
      {listing.publiclyReadable != null ? <ListingConfigurationField listing={listing} section="readership"
        label={intl.formatMessage({ id: "share.listings.readership" })} /> : null}
      <ListingMatchField ctx={ctx} />
      {listing.state === ListingState.Open ? (
        <ListingField label={<FormattedMessage id="share.listings.post.label" defaultMessage="Post" />}>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              id="listing-post-link"
              to={`/share/listings/${listing.id}/post`}
              className={buttonClasses({ variant: "secondary", size: "compact", className: "gap-1" })}
            >
              <FormattedMessage id="share.listings.post.open" defaultMessage="Post ansehen" />
              <span aria-hidden="true">↗</span>
            </Link>
            <ShareUrlButton path={`/share/open-listings/${listing.id}`}
              className={buttonClasses({ variant: "secondary", size: "compact" })} />
          </div>
        </ListingField>
      ) : null}
    </div>

    </div>
  );
}
